import React, { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/ui/table";
import Checkbox from "src/components/form-new/checkbox";
import BorderedInput from "src/components/form-new/bordered-input";
import { Panel } from "../../types/panel.types";
import BorderedSelect from "src/components/form-new/bordered-select";
import { Client } from "src/pages/client-management/types/client.types";
import { Download, Users } from "lucide-react";
import { Button } from "src/components/ui/button";
import { useGeneratePanelistsMutation } from "../../endpoints/panel.endpoints";
import notificationService from "src/services/notification/notification.service";
import { useDispatch } from "react-redux";

export interface ColumnData {
  active: boolean;
  name: string;
  samples: string[];
  description: string;
  fillPercentage: number;
}

export interface ParsedCSVData {
  columns: ColumnData[];
  rowCount: number;
  columnCount: number;
}

interface ManagePanelTableProps {
  panel: Panel;
  clients: Client[];
  onPanelChange?: (field: string, value: any) => void;
  onColumnsChange?: (id: string, field: string, value: any) => void;
}

const DATA_TYPE_OPTIONS = [
  { value: 'Int', label: 'Int' },
  { value: 'Float', label: 'Float' },
  { value: 'Boolean', label: 'Boolean' },
  { value: 'String', label: 'String' },
];

const PANEL_TYPE_OPTIONS = [
  { value: 'ai', label: 'AI' },
  { value: 'rtr', label: 'RTR' },
];

const ManagePanelTable: React.FC<ManagePanelTableProps> = ({
  panel,
  clients,
  onPanelChange,
  onColumnsChange,
}) => {
  const { fields } = panel;
  const dispatch = useDispatch();
  const [isGeneratingPanelists, setIsGeneratingPanelists] = useState(false);
  const [generatePanelists] = useGeneratePanelistsMutation();

  const handlePanelChange = (field: string, value: any) => {
    if (onPanelChange) {
      onPanelChange(field, value);
    }
  };

  const handleColumnChange = (index: number, field: string, value: any) => {
    if (onColumnsChange) {
      onColumnsChange(fields[index].id, field, value);
    }
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = panel.raw_dataset_url;
    link.setAttribute("download", panel.name + ".csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGeneratePanelists = async() => {
    try {
      const response = await generatePanelists({ panel_id: panel.id });
      if ('error' in response) {
        throw response.error;
      }
      notificationService.handleSuccess(panel, dispatch, 'Panelists generation task queued successfully');
      setIsGeneratingPanelists(true);
    } catch (error) {
      setIsGeneratingPanelists(false);
      notificationService.handleError(error, dispatch, 'Failed to queue panelists generation task');
      console.error('Failed to generate panelists:', error);
    }
  };

  if (fields.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 mt-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BorderedSelect
            label="Assign to Client"
            value={panel.client}
            onChange={(e) => handlePanelChange('client', e.target.value)}
            options={clients.map((client) => ({ value: client.ext_client_id, label: client.title }))}
          />
          <BorderedSelect
            label="Panel Type"
            value={panel.panel_type}
            className="w-[100px]"
            disabled={panel.locked}
            onChange={(e) => handlePanelChange('panel_type', e.target.value)}
            options={PANEL_TYPE_OPTIONS.map((panelType) => ({ value: panelType.value, label: panelType.label }))}
          />
        </div>
        <div className="flex items-center gap-2">
          {/* <Button
            className="hover:bg-gray-201 hover:text-gray-900"
            variant="outline"
            size="med"
            onClick={() => {}}
          >
            <Eye className="w-4 h-4" />
            View Sample
          </Button> */}
          <Button
            className="hover:bg-gray-201 hover:text-gray-900"
            variant="outline"
            size="med"
            disabled={panel.locked || isGeneratingPanelists}
            onClick={handleDownload}
          >
            <Download className="w-4 h-4" />
            Download
          </Button>
          <Button
            variant="outline"
            className="bg-blue-500 text-white hover:bg-blue-600 hover:text-white"
            size="med"
            disabled={panel.locked || isGeneratingPanelists}
            onClick={handleGeneratePanelists}
          >
            <Users className="w-4 h-4" />
            Generate Panelists
          </Button>
        </div>

      </div>
      <div className="flex items-center justify-between">
        <div />
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>
            <span className="font-medium text-gray-700">
              {fields.filter((field) => field.status === 'active').length}
            </span>
            {' '}
            active fields
          </span>
          <span>
            <span className="font-medium text-gray-700">
              {fields.length}
            </span>
            {' '}
            fields
          </span>
          <span>
            <span className="font-medium text-gray-700">
              {panel.panelists || 0}
            </span>
            {' '}
            panelists
          </span>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="">
              Active
            </TableHead>
            <TableHead>
              Column Name
            </TableHead>
            <TableHead>
              Fill Rate
            </TableHead>
            <TableHead>
              Data Type
            </TableHead>
            <TableHead>
              Sample Data
            </TableHead>
            <TableHead className="w-full">
              Description
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((field, index) => (
            <TableRow
              key={index}
              className="border-b border-gray-200"
            >
              <TableCell className="py-6 relative">
                {field.is_pii && (
                  <div className="flex flex-row items-center gap-1 absolute top-2 right-0 border border-gray-200 rounded-full px-1">
                    <div className="text-[8px] text-white bg-red-500 py-[6px] px-[6px] rounded-full" />
                    <span className="text-[10px] text-gray-500">
                      PII
                    </span>
                  </div>
                )}
                <Checkbox
                  disabled={panel.locked}
                  value={field.status === 'active' ? true : false}
                  onChange={(value) => handleColumnChange(index, 'status', value ? 'active' : 'archived')}
                />
              </TableCell>
              <TableCell className="py-6 font-semibold w-1/4">
                {field.display_name}
              </TableCell>
              <TableCell className="py-6">
                <span
                  className={`
                    inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                    ${
            field.fill_percentage >= 80
              ? 'bg-green-100 text-green-800'
              : field.fill_percentage >= 50
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }
                  `}
                >
                  {field.fill_percentage}
                  %
                </span>
              </TableCell>
              <TableCell className="py-6">
                <BorderedSelect
                  disabled={panel.locked}
                  value={DATA_TYPE_OPTIONS.find((option) => option.label === field.data_type)?.value || ''}
                  onChange={(e) => handleColumnChange(index, 'data_type', e.target.value)}
                  options={DATA_TYPE_OPTIONS}
                />
              </TableCell>
              <TableCell className="py-6 w-48">
                {field.sample_data.length > 0 && field.sample_data[0] === 'EMPTY' ? (
                  <span className="text-xs font-medium text-gray-400 italic">
                    EMPTY
                  </span>
                ) : (
                  <div className="flex gap-2">
                    {field.sample_data.slice(0, 2).map((sample, sampleIndex) => (
                      <span
                        key={sampleIndex}
                        className={`text-xs font-medium text-white truncate max-w-[150px] px-2 py-1 rounded-xl bg-gray-500`}
                        title={sample}
                      >
                        {sample}
                      </span>
                    ))}
                  </div>
                )}
              </TableCell>
              <TableCell className="py-6 w-full">
                <BorderedInput
                  disabled={panel.locked}
                  className="w-full"
                  value={field.description}
                  onBlur={(e) => handleColumnChange(index, 'description', e.target.value)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ManagePanelTable;

